import { useState, useEffect, useContext } from 'react';
import { CHARACTER_ART, getCharacterArt } from "./characterArtMap.js";
import { CHARACTERS } from "./characters.js";

import { ThemeContext } from './ThemeContext.jsx';
// ─── SPLASH SCREEN + LOADING STATES ─────────────────────────────────────────
// Cinematic app launch experience with character art cycling.
// Also exports smaller loading indicators for section transitions.
//
// Usage (replace existing authLoading block in SixGuysRP.jsx):
//   import { AppSplash, SectionLoader } from './SplashScreen.jsx';
//   if (authLoading) return <AppSplash />;
//   // For section loading:
//   <SectionLoader message="Loading stories..." />

// Pick random featured characters that have full art
const FEATURED_CHARS = Object.keys(CHARACTER_ART)
  .filter(id => CHARACTER_ART[id]?.main?.full)
  .sort(() => Math.random() - 0.5);

// ─── APP SPLASH (full-screen launch) ────────────────────────────────────────
export function AppSplash({ message = "My Fate, My Love" }) {
  const { theme: t } = useContext(ThemeContext);
  const [charIndex, setCharIndex] = useState(0);
  const [fadePhase, setFadePhase] = useState("in"); // "in" | "out"
  const [dotsFrame, setDotsFrame] = useState(0);

  // Cycle through character art every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFadePhase("out");
      setTimeout(() => {
        setCharIndex(prev => (prev + 1) % FEATURED_CHARS.length);
        setFadePhase("in");
      }, 600);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  // Animate loading dots
  useEffect(() => {
    const interval = setInterval(() => setDotsFrame(f => (f + 1) % 4), 400);
    return () => clearInterval(interval);
  }, []);

  const currentCharId = FEATURED_CHARS[charIndex] || FEATURED_CHARS[0];
  const charArt = getCharacterArt(currentCharId, "full") || getCharacterArt(currentCharId, "medium");
  const charData = CHARACTERS.find(c => c.id === currentCharId);
  const _dots = ".".repeat(dotsFrame);

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: t.bgDeepest,
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", zIndex: 9999,
    }}>
      <style>{`
        @keyframes mfml-splash-breathe {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }
        @keyframes mfml-splash-text-reveal {
          from { opacity: 0; letter-spacing: 16px; }
          to { opacity: 1; letter-spacing: 8px; }
        }
        @keyframes mfml-splash-subtitle {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mfml-splash-line {
          from { width: 0; }
          to { width: 60px; }
        }
        @keyframes mfml-splash-dot {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes mfml-splash-particle {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
        }
      `}</style>

      {/* Background character art (faded, blurred) */}
      {charArt && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${charArt})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          filter: "blur(3px) brightness(0.5)",
          opacity: fadePhase === "in" ? 0.45 : 0,
          transition: "opacity 0.6s ease",
          transform: "scale(1.1)",
        }} />
      )}

      {/* Radial vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 40%, transparent 30%, rgba(10,10,15,0.9) 75%)",
      }} />

      {/* Red atmospheric glow */}
      <div style={{
        position: "absolute",
        top: "30%", left: "50%",
        width: "400px", height: "400px",
        transform: "translate(-50%, -50%)",
        borderRadius: "50%",
        background: "radial-gradient(circle, var(--ds-accent-a1f, rgba(198,40,40,0.12)) 0%, transparent 70%)",
        animation: "mfml-splash-breathe 4s ease-in-out infinite",
      }} />

      {/* Floating particles */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute",
          bottom: "-10px",
          left: `${8 + (i * 7.5)}%`,
          width: `${2 + Math.random() * 3}px`,
          height: `${2 + Math.random() * 3}px`,
          borderRadius: "50%",
          background: i % 3 === 0 ? t.accentDanger : i % 3 === 1 ? t.accentGold : t.accentPrimary,
          animation: `mfml-splash-particle ${8 + Math.random() * 10}s linear ${Math.random() * 5}s infinite`,
          opacity: 0.4,
        }} />
      ))}

      {/* Center content */}
      <div style={{
        position: "relative", zIndex: 10,
        textAlign: "center",
        display: "flex", flexDirection: "column",
        alignItems: "center", gap: "8px",
      }}>
        {/* Logo */}
        <div style={{
          width: "90px", height: "90px",
          borderRadius: "50%",
          border: "2px solid var(--ds-accent-a59, rgba(198,40,40,0.35))",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "radial-gradient(circle, var(--ds-accent-a1a, rgba(198,40,40,0.1)) 0%, transparent 70%)",
          animation: "mfml-splash-breathe 3s ease-in-out infinite",
          marginBottom: "8px",
        }}>
          <img
            src="/art/brand/logo3_128.png"
            alt="MFML"
            className="emblem-idle"
            style={{ width: "48px", height: "48px", filter: "drop-shadow(0 0 12px var(--ds-accent-a66, rgba(198,40,40,0.4)))" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: "36px",
          fontFamily: "'Cinzel', serif",
          fontWeight: 700,
          color: t.accentDanger,
          letterSpacing: "8px",
          textTransform: "uppercase",
          margin: 0,
          animation: "mfml-splash-text-reveal 1.2s ease-out",
          textShadow: "0 0 30px var(--ds-accent-a66, rgba(198,40,40,0.4))",
        }}>
          MFML
        </h1>

        {/* Subtitle */}
        <p style={{
          color: t.textMuted,
          fontSize: "14px",
          letterSpacing: "3px",
          fontStyle: "italic",
          margin: 0,
          animation: "mfml-splash-subtitle 1s ease-out 0.4s both",
        }}>
          {message}
        </p>

        {/* Decorative line */}
        <div style={{
          height: "1px",
          background: "linear-gradient(90deg, transparent, var(--ds-accent-a66, #c6282866), transparent)",
          margin: "12px 0",
          animation: "mfml-splash-line 1.5s ease-out 0.6s both",
          overflow: "hidden",
          width: "60px",
        }} />

        {/* Character name (subtle) */}
        {charData && (
          <div style={{
            fontSize: "11px",
            color: "var(--ds-gold-a80, rgba(196,168,130,0.5))",
            letterSpacing: "2px",
            fontFamily: "'Cinzel', serif",
            opacity: fadePhase === "in" ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}>
            {charData.name}
          </div>
        )}

        {/* Loading dots */}
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: "6px", height: "6px",
              borderRadius: "50%",
              background: t.accentDanger,
              animation: `mfml-splash-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
            }} />
          ))}
        </div>
      </div>

      {/* Top + bottom accent lines */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "2px",
        background: "linear-gradient(90deg, transparent 10%, var(--ds-accent-a44, #c6282844) 30%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 70%, transparent 90%)",
      }} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "2px",
        background: "linear-gradient(90deg, transparent 10%, var(--ds-accent-a44, #c6282844) 30%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 70%, transparent 90%)",
      }} />
    </div>
  );
}

// ─── SECTION LOADER (inline loading for content areas) ──────────────────────
export function SectionLoader({ message = "Loading...", accentColor = "var(--ds-accent, #c62828)" }) {
  const { theme: t } = useContext(ThemeContext);
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "60px 24px", gap: "16px",
    }}>
      <style>{`
        @keyframes mfml-section-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        width: "32px", height: "32px",
        borderRadius: "50%",
        border: `2px solid ${accentColor}22`,
        borderTopColor: accentColor,
        animation: "mfml-section-spin 0.8s linear infinite",
      }} />
      <span style={{
        fontSize: "13px",
        color: t.textMuted,
        letterSpacing: "1px",
        fontStyle: "italic",
      }}>
        {message}
      </span>
    </div>
  );
}

export default AppSplash;
